import { usePSNavigate } from '../../hooks/usePSNavigate'
import { Dashboard } from '../../models/Dashboard'
import { Item } from '../../models/Item'
import { RecordFactory } from '../../models/RecordFactory'
import { ComponentData } from '../../utils/typing'
import { Icon } from '../Icon'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Avatar from '../app/design/Avatar'
import Label from '../app/design/Label'
import {
    ClickableTableRow,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../app/design/Table'
import Tag from '../app/design/Tag'
import { MoreMenuButton } from '../common/MoreMenuButton'
import { DashboardSkeleton } from '../common/skeletons/DashboardSkeleton'
import { EmptyDashboardLayout } from './EmptyDashboardLayout'
import { KeyMetric } from './KeyMetric'

type Props = {
    items: Item[]
    dashboard?: Dashboard
    searchText: string
    isLoading: boolean
}

function StockStatus({ count }: { count: number }) {
    const absCount = Math.abs(Math.trunc(count))
    if (count <= 0) return <Tag size="sm" intent="danger" label="Out of stock" />
    if (count < 10) return <Tag size="sm" intent="warning" label={`${absCount} remaining`} />
    return <Tag size="sm" intent="success" label={`${absCount} remaining`} />
}

const viewWillAppear = async (base: PocketComponent<Props>) => {
    return await Promise.all(
        base.props.items.map((i) => RecordFactory.getInstance().loadItem(i.id!))
    )
}

function render({
    base: {
        props: { items, dashboard, searchText, isLoading },
    },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>, Props>) {
    const navigate = usePSNavigate()
    const balances = dashboard?.metaData.valueForDictionaryKey('balances')

    if (isLoading) {
        return <DashboardSkeleton />
    }

    return (
        <div className="flex flex-col gap-6">
            <KeyMetric
                metrics={[
                    {
                        title: dashboard?.value === 1 ? `Item in inventory` : `Items in inventory`,
                        value: (dashboard?.value ?? 0).toString(),
                    },
                    {
                        title: dashboard?.count === 1 ? `Product` : `Products`,
                        value: (dashboard?.count ?? 0).toString(),
                    },
                ]}
            />
            <Table>
                <TableHeader>
                    <TableRow location="head">
                        <TableHead className="font-normal">Item</TableHead>
                        <TableHead className="font-normal">Status</TableHead>
                        <TableHead className="font-normal"></TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {items.map((i) => {
                        const count = balances && i.id ? balances[i.id] : 0
                        return (
                            <ClickableTableRow key={i.id} href={`/inventory/product/${i.id}`}>
                                <TableCell>
                                    <div className="flex items-center gap-4">
                                        {i.image?.uri ? (
                                            <Avatar size="sm">
                                                <img
                                                    src={i.image.uri}
                                                    alt={i.name}
                                                    className="size-9 rounded-full"
                                                />
                                            </Avatar>
                                        ) : (
                                            <Icon
                                                icon="package.small"
                                                background="round"
                                                className="size-9"
                                            />
                                        )}
                                        <Label size="sm">{i.name}</Label>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <StockStatus count={count} />
                                </TableCell>
                                <TableCell className="flex justify-end">
                                    <MoreMenuButton
                                        options={[
                                            {
                                                id: 'history',
                                                title: 'View change history',
                                                icon: 'clock',
                                            },
                                            {
                                                id: 'edit',
                                                title: 'Change quantity',
                                                icon: 'hash',
                                            },
                                        ]}
                                        onSelectOption={(option) => {
                                            switch (option.id) {
                                                case 'history':
                                                    navigate(`/inventory/history`, {
                                                        state: { item: i },
                                                    })
                                                    break
                                                case 'edit':
                                                    navigate(`/inventory/${i.id}/new`)
                                                    break
                                            }
                                        }}
                                    />
                                </TableCell>
                            </ClickableTableRow>
                        )
                    })}
                    {items.length === 0 && (
                        <TableCell colSpan={10}>
                            <EmptyDashboardLayout dashboard={dashboard} searchtext={searchText} />
                        </TableCell>
                    )}
                </TableBody>
            </Table>
        </div>
    )
}

export const InventoryDashboard = usePSComponent(render, viewWillAppear)
